<template>
  <div class="user-form">
    <section class="user-steps">
      <div class="container-fluid">
        <!-- Header -->
        <div class="row mb-4">
          <div class="col-12 d-flex align-items-center">
            <h5 class="mb-0 mr-3">
              {{ isEditMode ? "Editar Usuario" : "Nuevo Usuario" }}
            </h5>
            <router-link :to="route + 'users'">
              <img src="@/assets/img/backspace.svg" alt="backspace" width="30" />
            </router-link>
          </div>
        </div>

        <!-- Existing user notice -->
        <div v-if="userExistsWarning" class="alert alert-warning" role="alert">
          {{ userExistsWarning }}
        </div>

        <!-- Form -->
        <div class="card shadow-sm">
          <div class="card-body">
            <!-- Loading the record: the form is not shown until it is complete,
                 so nothing can be typed into fields about to be overwritten -->
            <div v-if="loadingUser" class="py-4 text-center">
              <div class="loading"></div>
              <p class="text-muted mb-0">Cargando datos del usuario...</p>
            </div>

            <form v-else @submit.prevent="saveUser">
              <!-- Identification -->
              <h6 class="mb-3 text-muted border-bottom pb-2">Identificación</h6>
              <div class="form-row">
                <div class="form-group col-md-6">
                  <label for="systemId">
                    {{ labels.systemId }} <span class="text-danger">*</span>
                  </label>
                  <div class="input-group">
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-valid': systemIdAvailable }"
                      id="systemId"
                      ref="systemId"
                      v-model="user.systemId"
                      :disabled="isEditMode"
                      :readonly="checking"
                      @blur="verifySystemId"
                      @keydown.enter.prevent="verifySystemId"
                      autocomplete="off"
                      required
                    />
                    <div class="input-group-append" v-if="!isEditMode">
                      <button
                        type="button"
                        class="btn btn-outline-primary"
                        :disabled="checking || !user.systemId"
                        @click="verifySystemId"
                      >
                        <span
                          v-if="checking"
                          class="btn-spinner"
                          aria-hidden="true"
                        ></span>
                        {{ checking ? "Verificando..." : "Verificar" }}
                      </button>
                    </div>
                  </div>
                  <small v-if="isEditMode" class="form-text text-muted">
                    El System ID identifica al distribuidor y no puede
                    modificarse.
                  </small>
                  <small
                    v-else-if="systemIdAvailable"
                    class="form-text text-success"
                  >
                    System ID disponible.
                  </small>
                  <small v-else class="form-text text-muted">
                    Ingresá el System ID y verificalo para habilitar el resto
                    del formulario.
                  </small>
                </div>
              </div>

              <!-- Everything else stays locked until the System ID is settled -->
              <fieldset :disabled="!fieldsEnabled">
                <h6 class="mt-4 mb-3 text-muted border-bottom pb-2">
                  Información General
                </h6>
                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label for="name">
                      {{ labels.name }} <span class="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      id="name"
                      ref="name"
                      v-model="user.name"
                      required
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <label for="country">
                      {{ labels.country }} <span class="text-danger">*</span>
                    </label>
                    <!-- an admin only manages its own country, so this is
                         filled in automatically and cannot be changed -->
                    <input
                      type="text"
                      class="form-control"
                      id="country"
                      :value="
                        loadingRefs
                          ? 'Cargando país...'
                          : selectedCountry
                          ? selectedCountry.name
                          : 'Sin país asignado'
                      "
                      disabled
                    />
                    <small class="form-text text-muted">
                      Sólo podés gestionar distribuidores de tu país.
                    </small>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group col-md-6">
                    <label for="Tipo_Usuario">{{ labels.Tipo_Usuario }}</label>
                    <v-select
                      v-if="userTypes.length || loadingRefs"
                      id="Tipo_Usuario"
                      :clearable="true"
                      v-model="user.Tipo_Usuario"
                      :options="userTypes"
                      :loading="loadingRefs"
                      :disabled="!fieldsEnabled || loadingRefs"
                      :placeholder="
                        loadingRefs ? 'Cargando opciones...' : 'Sin definir'
                      "
                    ></v-select>
                    <!-- falls back to free text if the option list is unreachable -->
                    <input
                      v-else
                      type="text"
                      class="form-control"
                      id="Tipo_Usuario"
                      v-model="user.Tipo_Usuario"
                    />
                  </div>
                  <div class="form-group col-md-6">
                    <label for="password">
                      {{ labels.password }}
                      <span v-if="!isEditMode" class="text-danger">*</span>
                    </label>
                    <input
                      type="password"
                      class="form-control"
                      id="password"
                      v-model="user.password"
                      :required="!isEditMode"
                      autocomplete="new-password"
                      :placeholder="isEditMode ? 'Dejar en blanco para mantenerla' : ''"
                    />
                    <small v-if="isEditMode" class="form-text text-muted">
                      Sólo completá este campo si querés asignar una contraseña
                      nueva.
                    </small>
                  </div>
                </div>

                <div class="form-row">
                  <div class="form-group col-md-6">
                    <div class="custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        class="custom-control-input"
                        id="web_order"
                        v-model="user.web_order"
                      />
                      <label class="custom-control-label" for="web_order">
                        {{ labels.web_order }}
                      </label>
                    </div>
                  </div>
                </div>

                <!-- Country Specific Fields -->
                <div v-if="visibleCountryFields.length">
                  <h6 class="mt-4 mb-3 text-muted border-bottom pb-2">
                    Configuración de {{ selectedCountryName }}
                  </h6>
                  <div class="form-row">
                    <div
                      v-for="field in visibleCountryFields"
                      :key="field.code"
                      :class="
                        field.type === 'boolean'
                          ? 'form-group col-md-3'
                          : 'form-group col-md-6'
                      "
                    >
                      <!-- checkbox -->
                      <div
                        v-if="field.type === 'boolean'"
                        class="custom-control custom-checkbox"
                      >
                        <input
                          type="checkbox"
                          class="custom-control-input"
                          :id="field.code"
                          v-model="user[field.code]"
                        />
                        <label class="custom-control-label" :for="field.code">
                          {{ field.label }}
                        </label>
                      </div>

                      <!-- select -->
                      <template v-else-if="field.type === 'select'">
                        <label :for="field.code">
                          {{ field.label }} <span class="text-danger">*</span>
                        </label>
                        <v-select
                          :id="field.code"
                          :clearable="false"
                          v-model="user[field.code]"
                          :options="field.options"
                          :disabled="!fieldsEnabled"
                          placeholder="Seleccioná una opción"
                        ></v-select>
                      </template>

                      <!-- text -->
                      <template v-else>
                        <label :for="field.code">
                          {{ field.label }} <span class="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          class="form-control"
                          :id="field.code"
                          v-model="user[field.code]"
                          required
                        />
                      </template>
                    </div>
                  </div>
                </div>
              </fieldset>

              <!-- Actions -->
              <div class="form-row mt-4">
                <div class="col-12 d-flex justify-content-end align-items-center">
                  <small v-if="!fieldsEnabled" class="text-muted mr-3">
                    Verificá el System ID para continuar.
                  </small>
                  <router-link
                    :to="route + 'users'"
                    class="btn btn-secondary mr-2"
                    >Cancelar</router-link
                  >
                  <button
                    type="submit"
                    class="btn btn-success"
                    :disabled="!fieldsEnabled || saving"
                  >
                    <span
                      v-if="saving"
                      class="btn-spinner"
                      aria-hidden="true"
                    ></span>
                    {{ saveLabel }}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
    <vue-basic-alert :duration="300" :closeIn="5000" ref="alert" />
  </div>
</template>

<script>
import userService from "@/services/userService";
import { route } from "@/enviroment";
import { redirectWithFlash } from "@/utils/flashAlert";

// Readable name for each profile field, so the form never shows internal codes
const FIELD_LABELS = {
  systemId: "System ID (Sold-to)",
  name: "Nombre",
  country: "País",
  password: "Contraseña",
  web_order: "Acceso a WOE (Web Order)",
  Tipo_Usuario: "Tipo de Usuario",
  ftl: "Full Truck Load (FTL)",
  ltl: "Less Than Truck Load (LTL)",
  pallet: "Pallets",
  temp_recorder: "Registrador de Temperatura",
  sleep_sheet: "Hoja de Descanso",
  consolidar_carga: "Consolidar Carga",
  without_idoc: "Sin generación de IDoc",
};

const emptyUser = () => ({
  systemId: "",
  name: "",
  country: null,
  web_order: false,
  password: "",
  Tipo_Usuario: "",
  ftl: false,
  ltl: false,
  pallet: false,
  temp_recorder: false,
  sleep_sheet: false,
  consolidar_carga: false,
  without_idoc: false,
});

export default {
  name: "UserForm",
  components: {
    vSelect: window["vue-select"],
  },
  data() {
    return {
      route: route,
      labels: FIELD_LABELS,
      // one flag per concern, so each one can drive its own control
      loadingRefs: false,
      loadingUser: false,
      checking: false,
      saving: false,
      userExistsWarning: "",
      // set when an existing systemId is detected on a create form
      existingUserLoaded: false,
      systemIdAvailable: false,
      // remembered so retyping the same value restores the result instead of
      // leaving the form locked with nothing to unlock it
      lastCheckedSystemId: null,
      lastCheckWasAvailable: false,
      countries: [],
      userTypes: [],
      user: emptyUser(),
    };
  },
  computed: {
    isEditMode() {
      return !!this.$route.params.id || this.existingUserLoaded;
    },
    // on creation nothing can be filled in before the System ID is settled:
    // otherwise the work is lost when an existing record overwrites the form
    fieldsEnabled() {
      return this.isEditMode || this.systemIdAvailable;
    },
    saveLabel() {
      if (this.saving) {
        return this.isEditMode ? "Actualizando..." : "Guardando...";
      }
      return this.isEditMode ? "Actualizar" : "Guardar";
    },
    selectedCountry() {
      return (
        this.countries.find(
          (item) => String(item.id) === String(this.user.country)
        ) || null
      );
    },
    selectedCountryName() {
      return this.selectedCountry
        ? this.selectedCountry.name.toUpperCase()
        : "";
    },
    // which fields apply is decided by the API, so the form does not keep its
    // own copy of the country to fields mapping
    countryFields() {
      return (this.selectedCountry && this.selectedCountry.fields) || [];
    },
    // a field with a condition is only asked for while its trigger holds
    visibleCountryFields() {
      return this.countryFields.filter((field) => this.conditionMet(field));
    },
    everyCountryField() {
      return this.countries.reduce(
        (fields, country) => fields.concat(country.fields || []),
        []
      );
    },
  },
  watch: {
    // editing the System ID invalidates a previous check, unless the value
    // ends up being the one already checked
    "user.systemId"(value) {
      if (this.isEditMode) return;

      if ((value || "").trim() === this.lastCheckedSystemId) {
        this.systemIdAvailable = this.lastCheckWasAvailable;
        return;
      }
      this.systemIdAvailable = false;
      this.userExistsWarning = "";
    },
    // indicators of the previous country stop applying, and the backend clears
    // them on save: reflect that here so the form shows what will be stored
    selectedCountryName(name) {
      if (!name) return;
      this.resetInapplicableFields();
    },
    // clearing a trigger clears what depended on it, mirroring the backend
    visibleCountryFields() {
      this.resetInapplicableFields();
    },
  },
  created() {
    this.loadReferenceData();
    if (this.$route.params.id) {
      this.loadUser(this.$route.params.id);
    }
  },
  methods: {
    async loadReferenceData() {
      this.loadingRefs = true;
      try {
        const [countries, userTypes] = await Promise.all([
          userService.getCountries(),
          userService.getUserTypes().catch((err) => {
            // the field falls back to free text, so this is not worth an alert
            console.error("Error loading user types", err);
            return null;
          }),
        ]);
        this.countries = countries.data.data || [];
        // the endpoint only returns the administrator country, so it is filled
        // in without asking; on edit the loaded record already carries it
        if (!this.user.country && this.countries.length) {
          this.user.country = this.countries[0].id;
        }
        if (userTypes) {
          this.userTypes = this.normalizeUserTypes(userTypes.data.data);
        }
      } catch (err) {
        console.error("Error loading countries", err);
        this.showAlert("error", "Error", "No se pudieron cargar los países.");
      } finally {
        this.loadingRefs = false;
      }
    },
    conditionMet(field) {
      if (!field.requires) return true;
      return Object.keys(field.requires).every(
        (trigger) => this.user[trigger] === field.requires[trigger]
      );
    },
    emptyValueFor(field) {
      return field.type === "boolean" ? false : "";
    },
    /**
     * Fields of another country, and fields whose condition stopped holding,
     * are emptied: the backend clears them on save, so the form has to show
     * what will actually be stored.
     */
    resetInapplicableFields() {
      const visible = this.visibleCountryFields.map((field) => field.code);

      this.everyCountryField.forEach((field) => {
        if (!visible.includes(field.code)) {
          this.user[field.code] = this.emptyValueFor(field);
        } else if (this.user[field.code] === undefined) {
          this.user[field.code] = this.emptyValueFor(field);
        }
      });
    },
    // Core returns the custom item list keyed by position, not as an array:
    // {"0": "Distribuidor", "1": "Operador", "2": "Supermercado"}
    normalizeUserTypes(data) {
      return Object.values(data || {}).filter(
        (item) => typeof item === "string" && item !== ""
      );
    },
    async loadUser(systemId) {
      this.loadingUser = true;
      try {
        const resp = await userService.getUser(systemId);
        // password is never returned: it is only sent when being changed
        this.user = Object.assign(emptyUser(), resp.data.data, {
          password: "",
        });
      } catch (err) {
        console.error("Error loading user", err);
        this.showAlert(
          "error",
          "Error",
          "No se pudieron cargar los datos del usuario."
        );
      } finally {
        this.loadingUser = false;
      }
    },
    async verifySystemId() {
      if (this.isEditMode || this.checking) return;

      const systemId = (this.user.systemId || "").trim();
      if (!systemId) return;

      // already answered for this value: restore it without asking again
      if (systemId === this.lastCheckedSystemId) {
        this.systemIdAvailable = this.lastCheckWasAvailable;
        return;
      }

      this.checking = true;
      try {
        const resp = await userService.checkUserExists(systemId);
        const { exists, editable } = resp.data.data;
        this.lastCheckedSystemId = systemId;
        this.lastCheckWasAvailable = !exists;

        if (exists && editable) {
          this.userExistsWarning =
            "El usuario ya existe. Puede editar sus datos a continuación.";
          this.existingUserLoaded = true;
          await this.loadUser(systemId);
        } else if (exists) {
          // taken by a distributor of another country: nothing to edit here
          this.userExistsWarning =
            "Ese System ID ya está en uso por un distribuidor de otro país.";
          this.systemIdAvailable = false;
        } else {
          this.userExistsWarning = "";
          this.systemIdAvailable = true;
          this.focusField("name");
        }
      } catch (err) {
        console.error("Error checking user", err);
        this.showAlert(
          "error",
          "Error",
          "No se pudo verificar el System ID. Intentá nuevamente."
        );
      } finally {
        this.checking = false;
      }
    },
    focusField(ref) {
      this.$nextTick(() => {
        const field = this.$refs[ref];
        if (field && field.focus) {
          field.focus();
        }
      });
    },
    buildPayload() {
      const payload = Object.assign({}, this.user, {
        // lets the API scope country specific validations by readable name
        countryName: this.selectedCountryName,
      });
      // an empty password means "keep the current one"
      if (!payload.password) {
        delete payload.password;
      }
      return payload;
    },
    async saveUser() {
      if (this.saving) return;
      this.saving = true;

      const payload = this.buildPayload();

      try {
        if (this.isEditMode) {
          await userService.updateUser(this.user.systemId, payload);
          redirectWithFlash(this.$router, this.route + "users", {
            type: "success",
            title: "Éxito",
            msg: "Los datos del usuario se actualizaron correctamente.",
          });
        } else {
          await userService.createUser(payload);
          redirectWithFlash(this.$router, this.route + "users", {
            type: "success",
            title: "Éxito",
            msg: "Usuario creado correctamente.",
          });
        }
      } catch (err) {
        console.error(err);
        this.showAlert("error", "Error", this.errorMessage(err));
        this.saving = false;
      }
    },
    errorMessage(err) {
      const message =
        err.response && err.response.data ? err.response.data.message : "";
      if (
        message &&
        message.includes("The following credential already exists: systemId")
      ) {
        return "El usuario ya existe.";
      }
      return message || "Ocurrió un error al guardar.";
    },
    showAlert(type, title, message) {
      this.$refs.alert.showAlert(type, message, title);
    },
  },
};
</script>

<style scoped>
.user-steps {
  padding: 20px 0;
}

/* the locked section stays readable, but visibly not editable yet */
fieldset[disabled] {
  opacity: 0.55;
}

/* Bootstrap 4.1 predates .spinner-border, so the inline one is defined here */
.btn-spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  margin-right: 0.4rem;
  vertical-align: text-bottom;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: btn-spin 0.75s linear infinite;
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
